import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { isAPIApplicationException } from '../helpers';
import { AuthService } from '../../features/auth/services/auth.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private readonly _snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService,
        private readonly _authService: AuthService
    ) {}

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse) {
                    let errorMessage: string;
                    let statusCodeHandled = false;

                    if (error.error instanceof ErrorEvent) {
                        // client-side error
                        errorMessage = error.error.message;
                    } else {
                        // server-side error
                        statusCodeHandled = this.handleStatusCode(error);
                        errorMessage = error.error?.title ?? error.message;
                    }

                    // Show a toast message of the error message.
                    // TODO: Do we want to configure this snackBar to look more error like?  Include error icon?
                    if (!statusCodeHandled) this.showErrorMessage(errorMessage);
                } else {
                    // TODO: Unanticipated error type.  Investigate how this error came to be and adjust this logic to anticipate it in the future.
                    if (!environment.production)
                        console.warn('Unanticipated error occurred:', error);
                }

                // We still want to re-throw the error.  We are going to do application-wide
                // side effects to errors from HTTP calls here.  This should NOT handle specific
                // side effects we want our individual components to have.  If a component needs
                // to do something when an error occurs, we allow the error to propagate back
                // to the original subscriber/caller so that additional side effects can take place
                // in their scope of concern.
                // Exception: 401 Unauthorized returns EMPTY instead to prevent unhandled errors
                // from downstream error handling while the session expiration flow takes over.
                if (error instanceof HttpErrorResponse && error.status === 401)
                    return EMPTY;

                return throwError(error);
            })
        );
    }

    private showErrorMessage(errorMessage: string, duration = 4000) {
        this._snackBar.open(errorMessage, undefined, {
            duration
        });
    }

    /**
     * If we want to handle different status codes slightly differently,
     * this is where we can have that behavior defined.
     */
    private handleStatusCode(error: HttpErrorResponse): boolean {
        let handled = false;

        // 401 Unauthorized means the session is no longer valid (token expired, removed, or server-side logout).
        // Clear the session and trigger the "session expired" flow, which shows a snackbar with sign-in action.
        if (error.status === 401) {
            this._authService.handleSessionExpired();
            return true;
        }

        // 429 (Too Many Requests) is surfaced to end-users in every environment: an OTP/rate-limit
        // block must not fail silently. Covers both the per-address throttle (429 + retryAfterSeconds)
        // and the per-IP rate limiter's 429.
        if (error.status === 429) {
            this.showErrorMessage(
                this.buildTooManyRequestsMessage(error),
                6000
            );
            return true;
        }

        // Don't do this in production.  This is for developers.  End-users don't need to see this.
        if (!environment.production) {
            switch (error.status) {
                case 400:
                    console.warn('BadRequest from API:', error);
                    break;
                case 401:
                    console.warn('Unauthorized from API:', error);
                    break;
                case 403:
                    console.warn('Forbidden from API:', error);
                    break;
                case 0:
                case -1:
                    handled = true;
                    this.showErrorMessage('Unable to contact server.');
                    break;
                case 500:
                    break;
                default:
                    console.warn('Unanticipated http status code:', error);
                    break;
            }
        }
        return handled;
    }

    /**
     * Build the friendly "too many requests" message, interpolating the wait time from the
     * Retry-After header (or the body's retryAfterSeconds) when available.
     */
    private buildTooManyRequestsMessage(error: HttpErrorResponse): string {
        const retryAfterSeconds = this.getRetryAfterSeconds(error);
        if (retryAfterSeconds && retryAfterSeconds > 0) {
            const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
            return this._transloco.translate('auth.snack.too-many-requests', {
                minutes
            });
        }
        return this._transloco.translate(
            'auth.snack.too-many-requests-no-wait'
        );
    }

    private getRetryAfterSeconds(error: HttpErrorResponse): number | undefined {
        const header = error.headers?.get('Retry-After');
        if (header) {
            const parsed = Number(header);
            if (!Number.isNaN(parsed)) return parsed;
        }
        const body: any = error.error;
        if (body && typeof body.retryAfterSeconds === 'number')
            return body.retryAfterSeconds;
        return undefined;
    }
}
